/**
 * BowerIcon — Tintable inline-SVG icon component.
 *
 * The icon SVGs live in `assets/bower-icons/{16,20,40}/` — three pixel-grid
 * sizes designed to render crisply at the matching pixel dimension. The
 * component picks the closest size based on the requested render size.
 *
 * Tinting works via inline SVG + `currentColor`: at module load each SVG's
 * literal `#161616` strokes are rewritten to `currentColor`, so the
 * rendered SVG inherits the surrounding text colour. Pass `color="white"`
 * (or any Chakra colour token) to tint a specific instance.
 *
 * Default colour is `text.primary` via `currentColor`, which auto-switches
 * between light and dark mode.
 *
 * `gradient` swaps that flat tint for the brand ramp. It works by overriding
 * exactly the attributes `tintSvg` normalised — `[stroke="currentColor"]` and
 * `[fill="currentColor"]` — with a `url(#…)` paint server. Note the assets
 * carry `fill="none"` on the root and on unfilled counters, which is why the
 * override is attribute-scoped rather than `& svg *`: a CSS property beats a
 * presentation attribute, so a blanket rule would fill every counter in.
 * Identity icons only — see `theme/iconGradient.ts`.
 */

import { Box } from '@chakra-ui/react';
import type { BoxProps } from '@chakra-ui/react';
import { iconGradientSvgSx, type IconGradientVariant } from '../../theme/iconGradient';

// ── Asset imports (raw SVG markup as strings) ───────────────────────────────
// Using Vite's `?raw` import to inline SVG content. This lets us inject
// the SVG into the DOM directly so `currentColor` works without CSS masks.

import artifact16 from '../../assets/bower-icons/16/artifact.svg?raw';
import attachment16 from '../../assets/bower-icons/16/attachment.svg?raw';
import equipment16 from '../../assets/bower-icons/16/equipment.svg?raw';
import bird16 from '../../assets/bower-icons/16/bird.svg?raw';
import chat16 from '../../assets/bower-icons/16/chat.svg?raw';
import collection16 from '../../assets/bower-icons/16/collection.svg?raw';
import import16 from '../../assets/bower-icons/16/import.svg?raw';
import newNote16 from '../../assets/bower-icons/16/new-note.svg?raw';
import note16 from '../../assets/bower-icons/16/note.svg?raw';
import photo16 from '../../assets/bower-icons/16/photo.svg?raw';
import protocol16 from '../../assets/bower-icons/16/protocol.svg?raw';
import spreadsheet16 from '../../assets/bower-icons/16/spreadsheet.svg?raw';
import search16 from '../../assets/bower-icons/16/search.svg?raw';
import subCollection16 from '../../assets/bower-icons/16/sub-collection.svg?raw';
import text16 from '../../assets/bower-icons/16/text.svg?raw';
import voice16 from '../../assets/bower-icons/16/voice.svg?raw';
import workspace16 from '../../assets/bower-icons/16/workspace.svg?raw';

import artifact20 from '../../assets/bower-icons/20/artifact.svg?raw';
import attachment20 from '../../assets/bower-icons/20/attachment.svg?raw';
import equipment20 from '../../assets/bower-icons/20/equipment.svg?raw';
import bird20 from '../../assets/bower-icons/20/bird.svg?raw';
import chat20 from '../../assets/bower-icons/20/chat.svg?raw';
import collection20 from '../../assets/bower-icons/20/collection.svg?raw';
import import20 from '../../assets/bower-icons/20/import.svg?raw';
import newNote20 from '../../assets/bower-icons/20/new-note.svg?raw';
import note20 from '../../assets/bower-icons/20/note.svg?raw';
import photo20 from '../../assets/bower-icons/20/photo.svg?raw';
import protocol20 from '../../assets/bower-icons/20/protocol.svg?raw';
import spreadsheet20 from '../../assets/bower-icons/20/spreadsheet.svg?raw';
import search20 from '../../assets/bower-icons/20/search.svg?raw';
import subCollection20 from '../../assets/bower-icons/20/sub-collection.svg?raw';
import text20 from '../../assets/bower-icons/20/text.svg?raw';
import voice20 from '../../assets/bower-icons/20/voice.svg?raw';
import workspace20 from '../../assets/bower-icons/20/workspace.svg?raw';

import artifact40 from '../../assets/bower-icons/40/artifact.svg?raw';
import attachment40 from '../../assets/bower-icons/40/attachment.svg?raw';
import equipment40 from '../../assets/bower-icons/40/equipment.svg?raw';
import bird40 from '../../assets/bower-icons/40/bird.svg?raw';
import chat40 from '../../assets/bower-icons/40/chat.svg?raw';
import collection40 from '../../assets/bower-icons/40/collection.svg?raw';
import import40 from '../../assets/bower-icons/40/import.svg?raw';
import newNote40 from '../../assets/bower-icons/40/new-note.svg?raw';
import note40 from '../../assets/bower-icons/40/note.svg?raw';
import photo40 from '../../assets/bower-icons/40/photo.svg?raw';
import protocol40 from '../../assets/bower-icons/40/protocol.svg?raw';
import spreadsheet40 from '../../assets/bower-icons/40/spreadsheet.svg?raw';
import search40 from '../../assets/bower-icons/40/search.svg?raw';
import subCollection40 from '../../assets/bower-icons/40/sub-collection.svg?raw';
import text40 from '../../assets/bower-icons/40/text.svg?raw';
import voice40 from '../../assets/bower-icons/40/voice.svg?raw';
import workspace40 from '../../assets/bower-icons/40/workspace.svg?raw';

// ── Icon name type ──────────────────────────────────────────────────────────

export type BowerIconName =
  | 'workspace'
  | 'collection'
  | 'sub-collection'
  | 'note'
  | 'attachment'
  | 'artifact'
  | 'equipment'
  | 'bird'
  | 'chat'
  | 'import'
  | 'photo'
  | 'search'
  | 'text'
  | 'voice'
  | 'new-note'
  | 'protocol'
  | 'spreadsheet';

// ── Icon registry ───────────────────────────────────────────────────────────

interface IconAssets {
  16: StaticSvgMarkup;
  20: StaticSvgMarkup;
  40: StaticSvgMarkup;
}

/**
 * Branded type for SVG markup that has been verified to come from a
 * compile-time-only source (Vite `?raw` import). Stamped exclusively by
 * `tintSvg()`; consumers can demand this type to make accidental misuse
 * (passing a runtime-fetched / user-supplied string into
 * `dangerouslySetInnerHTML`) a compile error rather than a stored-XSS
 * vector. If you ever need to render dynamic SVG, do NOT add another
 * stamper — sanitise via DOMPurify first and use a different code path.
 */
type StaticSvgMarkup = string & { readonly __staticSvg: unique symbol };

/** Rewrite the SVG's hardcoded ink colour to `currentColor` so the icon
 *  inherits the surrounding text colour. Run once per asset at module load.
 *  Returns a `StaticSvgMarkup` brand to gate the unsafe inner-HTML render. */
function tintSvg(raw: string): StaticSvgMarkup {
  // Match #161616 in any case, both as fill and stroke. Some icons use
  // `fill-opacity` / `stroke-opacity` so opacity is preserved automatically.
  return raw.replace(/#161616/gi, 'currentColor') as StaticSvgMarkup;
}

const ICON_REGISTRY: Record<BowerIconName, IconAssets> = {
  workspace: { 16: tintSvg(workspace16), 20: tintSvg(workspace20), 40: tintSvg(workspace40) },
  collection: { 16: tintSvg(collection16), 20: tintSvg(collection20), 40: tintSvg(collection40) },
  'sub-collection': {
    16: tintSvg(subCollection16),
    20: tintSvg(subCollection20),
    40: tintSvg(subCollection40),
  },
  note: { 16: tintSvg(note16), 20: tintSvg(note20), 40: tintSvg(note40) },
  attachment: { 16: tintSvg(attachment16), 20: tintSvg(attachment20), 40: tintSvg(attachment40) },
  artifact: { 16: tintSvg(artifact16), 20: tintSvg(artifact20), 40: tintSvg(artifact40) },
  equipment: { 16: tintSvg(equipment16), 20: tintSvg(equipment20), 40: tintSvg(equipment40) },
  bird: { 16: tintSvg(bird16), 20: tintSvg(bird20), 40: tintSvg(bird40) },
  chat: { 16: tintSvg(chat16), 20: tintSvg(chat20), 40: tintSvg(chat40) },
  import: { 16: tintSvg(import16), 20: tintSvg(import20), 40: tintSvg(import40) },
  photo: { 16: tintSvg(photo16), 20: tintSvg(photo20), 40: tintSvg(photo40) },
  search: { 16: tintSvg(search16), 20: tintSvg(search20), 40: tintSvg(search40) },
  text: { 16: tintSvg(text16), 20: tintSvg(text20), 40: tintSvg(text40) },
  voice: { 16: tintSvg(voice16), 20: tintSvg(voice20), 40: tintSvg(voice40) },
  'new-note': { 16: tintSvg(newNote16), 20: tintSvg(newNote20), 40: tintSvg(newNote40) },
  protocol: { 16: tintSvg(protocol16), 20: tintSvg(protocol20), 40: tintSvg(protocol40) },
  spreadsheet: {
    16: tintSvg(spreadsheet16),
    20: tintSvg(spreadsheet20),
    40: tintSvg(spreadsheet40),
  },
};

/**
 * Every Bower glyph that actually ships, as a runtime list.
 *
 * DERIVED from the registry above rather than restated, so it cannot drift
 * from the assets. `BowerIconName` types the literals at compile time; this
 * exists for the registries that DERIVE a name — a map lookup, a fallback, a
 * value out of `@bower/shared` — where the compiler has nothing to check and
 * only a runtime assertion can (see `theme/__tests__/glyphRegistry.test.ts`).
 *
 * @internal Test seam: glyphRegistry.test asserts every derived Bower glyph ships.
 */
export const BOWER_ICON_NAMES: ReadonlyArray<BowerIconName> = Object.keys(
  ICON_REGISTRY,
) as BowerIconName[];

// ── Size selection ──────────────────────────────────────────────────────────

/**
 * Pick the pixel-grid asset closest to (but not exceeding) the requested
 * size. Falls back to 20 if the size string can't be parsed (e.g. "1rem").
 */
function pickSizeAsset(size: string | number): keyof IconAssets {
  const px = typeof size === 'number' ? size : parseFloat(size);
  if (!Number.isFinite(px)) return 20;
  if (px <= 16) return 16;
  if (px <= 24) return 20;
  return 40;
}

// ── Component ───────────────────────────────────────────────────────────────

interface BowerIconProps extends Omit<BoxProps, 'children' | 'color'> {
  /** Icon name from the registry */
  name: BowerIconName;
  /**
   * Icon size — accepts CSS values ("16px", "1.25rem") or a number (px).
   * Defaults to "20px". The component picks the closest pixel-grid asset
   * (16 / 20 / 40) for crisp rendering.
   */
  size?: string | number;
  /** Alt text for accessibility. If empty, marks the icon decorative. */
  alt?: string;
  /**
   * Tint colour. Accepts any Chakra color token (semantic preferred).
   * Defaults to `currentColor` so the icon inherits its parent's text
   * colour — set `color="…"` on a parent to tint a group of icons.
   *
   * Ignored when `gradient` is set: a paint server replaces the tint.
   */
  color?: string;
  /**
   * Paint the icon in the brand gradient instead of a flat tint.
   *
   * ONLY for identity icons — entity types in trees, tables, lists and cards.
   * Utility and affordance glyphs stay flat. `theme/iconGradient.ts` carries
   * the full rule and the reason; the short version is that the gradient does
   * not clear 3:1 in light mode, so it is only safe where a visible text label
   * sits beside the glyph and carries the meaning.
   *
   * `cool` is for magnifying-glass-shaped glyphs, which the warm ramp turns
   * into lollipops.
   */
  gradient?: IconGradientVariant;
}

export const BowerIcon: React.FC<BowerIconProps> = ({
  name,
  size = '20px',
  alt = '',
  color = 'currentColor',
  gradient,
  sx,
  ...rest
}) => {
  const assetKey = pickSizeAsset(size);
  const assets = ICON_REGISTRY[name];
  // Say what is wrong. Several callers reach this through a registry typed
  // `string` (`SourceDescriptor.icon`, the command palette's `entry.icon`,
  // attachment kinds) and cast on the way in, so an unknown name is reachable
  // — and without this it surfaced as `Cannot read properties of undefined`
  // from inside the lookup, naming neither the glyph nor the component.
  if (!assets) throw new Error(`BowerIcon: no asset named "${name}"`);
  const svgMarkup = assets[assetKey];

  return (
    <Box
      as="span"
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      // `display: block` (within an inline `<span>`) means the icon is a
      // proper block-level element with no inline baseline shenanigans —
      // matches MaterialIcon's positioning when both are placed in a flex
      // parent. Avoids the "icon sits slightly lower than its neighbours"
      // bug caused by inline-flex baseline alignment.
      display="block"
      width={size}
      height={size}
      color={color}
      flexShrink={0}
      pointerEvents="none"
      sx={{
        '& svg': {
          width: '100%',
          height: '100%',
          display: 'block',
        },
        ...(gradient ? iconGradientSvgSx(gradient) : null),
        ...sx,
      }}
      // SAFE: `svgMarkup` is a static string from a Vite `?raw` import at
      // module load — sourced from the trusted assets/bower-icons/ folder
      // we ship in the bundle. Never extend this pattern with runtime-
      // fetched SVG (user uploads, third-party CDN, etc.) without first
      // sanitising via DOMPurify or similar — `dangerouslySetInnerHTML`
      // executes embedded <script> tags and event-handler attributes.
      dangerouslySetInnerHTML={{ __html: svgMarkup }}
      {...rest}
    />
  );
};
